import React from 'react';
import { SubscriptionData } from '../BillingTypes';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { calculateCurrentSubscriptionAmount } from '../functions/billingUtils';

interface NextInvoiceCardProps {
    loading: string;
    subscription: SubscriptionData,
};

const NextInvoiceCard: React.FC<NextInvoiceCardProps> = ({ loading, subscription }) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();

    // Get the next invoice date
    const getNextInvoiceDate = (subscription: SubscriptionData) => {

        // Convert the end date of the subscription to a date
        const nextInvoiceDate = new Date(subscription.end_date!);

        // Add one day to the date
        nextInvoiceDate.setDate(nextInvoiceDate.getDate() + 1);

        // Format the date based on the user locale
        const formattedDate = nextInvoiceDate.toLocaleDateString(userLocale, { 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
        });

        return formattedDate;
    };

    // Get the next invoice amount
    const getNextInvoiceAmount = (subscription: SubscriptionData) => {

        // Initialize the invoice amount variable
        let invoiceAmount;

        // If a pending subscription is given, calculate the invoice amount based on the pending subscription
        if (subscription.pending_subscription?.subscription_lines) {
            invoiceAmount = calculateCurrentSubscriptionAmount({
                subscription_lines: subscription.pending_subscription.subscription_lines, 
                displayPrice: subscription.pending_subscription.duration, 
                returnAmount: 'totalInclVat',
                userLocale, 
                useDisplayPrice: true
            });
        }
        
        // Otherwise, calculate the invoice amount based on the current subscription
        else if (subscription.subscription_lines) {
            invoiceAmount = calculateCurrentSubscriptionAmount({
                subscription_lines: subscription.subscription_lines, 
                displayPrice: subscription.duration, 
                returnAmount: 'totalInclVat', 
                userLocale,
                useDisplayPrice: true
            });
        };

        return invoiceAmount;
    };

    return (
        subscription.status !== 'cancelled' && !subscription.is_trial ? (
            <div className='card next-invoice'>
                <h6>{t('billing.general.next_invoice_label')}</h6>
                {loading === 'show-loader' ? (
                    <div className="loader"></div>
                ) : loading === 'success' && (
                    <div className='next-invoice-details'>
                        <div className=''>
                            <p>{getNextInvoiceDate(subscription)}</p>
                            <p>{t('billing.general.next_invoice_estimated_price_label')}</p>
                        </div>
                        <div className='price-big'>
                            € {getNextInvoiceAmount(subscription)}
                        </div>
                    </div>
                )}
            </div>
        ) : null
    )
};

export default NextInvoiceCard;